import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isPlainObject, get as safeGet } from 'lodash';
import { styled, ClassNames } from '@kufox/mui/emotion';
import { useTranslation } from '@tools/i18n';
import Button from '@kufox/mui/Button';
import Input from '@kufox/mui/Input';
import { InfoOutlined } from '@kufox/icons';
import Tooltip from '@kufox/mui/Tooltip';
import useTheme from '@kufox/mui/hooks/useTheme';

import { NAMESPACE, PHONE_BIZTYPE, EMAIL_BIZTYPE, SIGNUP_TYPE_CONFIG } from '../constants';
import { useToast, useLang } from '../../hookTool';
import VoiceCode from '../../components/NewVoiceCode';
import {
  kcsensorsClick,
  kcsensorsManualTrack,
  getTrackingSource,
  removeSpaceSE,
} from '../../common/tools';

const Wrap = styled.div`
  width: 480px;
  & input {
    width: auto;
    flex: 1;
  }
`;
const Title = styled.h2`
  font-size: 34px;
  font-weight: 500;
  line-height: 32px;
  margin: 0 0 40px 0;
`;

const Tips = styled.p`
  color: ${(props) => props.theme.colors.text60};
  font-weight: 400;
  font-size: 16px;
  line-height: 26px;
  margin: 0;
`;
const InputPreTips = styled.div`
  color: ${(props) => props.theme.colors.text60};
  font-weight: 400;
  font-size: 14px;
  line-height: 22px;
  margin: 34px 0 4px;
`;

const RepeatSend = styled.div`
  color: ${(props) => {
    if (props.disable) return 'rgba(1, 8, 30, 0.38)';
    return props.theme.colors.primary;
  }};
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  cursor: pointer;
`;

const CodeSent = styled.div`
  display: flex;
  align-items: center;
  color: ${(props) => props.theme.colors.text20};
`;

const SentText = styled.span`
  margin-right: 4px;
`;

const InfoIconContainer = styled.span`
  display: flex;
  align-items: center;
`;

const ErrorText = styled.div`
  font-size: 14px;
  color: ${(props) => props.theme.colors.secondary};
`;

let timer = null;

const AccountActive = ({
  isKumex,
  fromDrawer,
  onChange,
  trackingConfig = {},
  recallType = null,
}) => {
  const toast = useToast();
  const { t: _t } = useLang();
  const { t } = useTranslation('entrance');
  const {
    loading,
    sendChannel,
    EMAIL_REGISTER_countTime,
    PHONE_REGISTER_countTime,
    FirstStepData,
    registerType,
    sendCodeSuc,
  } = useSelector((state) => state[NAMESPACE]);
  const theme = useTheme();
  const [forbidSentSeconds, setForbidSentSeconds] = useState(120);
  const [inputVal, setInputVal] = useState('');
  const dispatch = useDispatch();
  const [disable, setDisable] = useState(false);
  const [isFocus, setIsFoucs] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);

  // 组装需要的数据
  const { phone, email, password, referralCode, countryCode } = FirstStepData || {};
  const isRegisterByPhone = registerType === 'phone';
  const account = isRegisterByPhone ? `+${countryCode}${phone}` : email;
  const sendCodeData = isRegisterByPhone
    ? { phone, bizType: PHONE_BIZTYPE, countryCode, sendChannel: 'SMS', toast }
    : { email, bizType: EMAIL_BIZTYPE, toast };
  const postRecallParams = isRegisterByPhone
    ? {
        countryCode,
        language: navigator.language,
        phone,
        type: recallType,
      }
    : {
        language: navigator.language,
        type: recallType,
        email: removeSpaceSE(email),
      };
  const countdownTime = isRegisterByPhone
    ? PHONE_REGISTER_countTime.time
    : EMAIL_REGISTER_countTime.time;
  const countdownRef = useRef({ time: countdownTime });
  countdownRef.current = { time: countdownTime };
  const { validationType } = SIGNUP_TYPE_CONFIG[registerType];
  const submitPayload = {
    [registerType]: isRegisterByPhone ? phone : email,
    password,
    code: inputVal,
    validationType,
    toast,
    countryCode,
    referralCode,
    t,
    isKumex, // kumex 需要注册时直接开通合约
  };
  const noInputVal = removeSpaceSE(inputVal).length <= 0;

  // 记住，否则语音验证子组件倒计时会有问题
  const offDisabled = useCallback(() => {
    setDisable(false);
    dispatch({
      type: `${NAMESPACE}/update`,
      payload: {
        PHONE_REGISTER_countTime: { time: 0 },
      },
    });
  }, []);

  // 倒计时运作
  const handleTimer = () => {
    clearInterval(timer);
    setForbidSentSeconds(countdownRef.current.time);

    timer = setInterval(() => {
      setForbidSentSeconds((v) => {
        if (v <= 0) {
          offDisabled();
          clearInterval(timer);
          return v;
        }
        return v - 1;
      });
    }, 1000);
  };

  const handleSendCode = async () => {
    dispatch({
      type: `${NAMESPACE}/sendVerifyCode`,
      payload: sendCodeData,
    });
    // 注册召回接口调用
    if (recallType !== null) {
      dispatch({
        type: `${NAMESPACE}/${SIGNUP_TYPE_CONFIG[registerType].recallAPI}`,
        payload: postRecallParams,
      });
    }
    kcsensorsClick(['resentCode', '1']);
  };

  // 提交注册
  const handleSubmit = async () => {
    const blockID = 'activeConfirm';
    if (fromDrawer) {
      kcsensorsManualTrack(
        {
          spm: [blockID, '1'],
          data: {
            pagecate: 'sideRegister',
            ...safeGet(trackingConfig, 'data', {}),
          },
        },
        'page_click',
      );
    } else {
      kcsensorsClick([blockID, '1']);
    }
    if (noInputVal) {
      setIsFoucs(true);
      return;
    }
    setSubmitLoading(true);
    const trackSource = getTrackingSource(trackingConfig);
    const isOk = await dispatch({
      type: `${NAMESPACE}/signUpFlow`,
      payload: submitPayload,
      trackResultParams: {
        pagecate: 'registerV2',
        source: trackSource,
        referral_code: referralCode,
        is_futures_referral: isKumex,
      },
    });
    setSubmitLoading(false);
    if (isOk) {
      dispatch({ type: `${NAMESPACE}/resetInit` });
      let userInfo = isOk;
      if (userInfo !== false && isPlainObject(userInfo)) {
        userInfo.$$blockID = blockID;
      }
      onChange(userInfo);
    }
  };

  const onSendVoice = () => {
    return dispatch({
      type: `${NAMESPACE}/sendVerifyCode`,
      payload: {
        countryCode,
        phone: removeSpaceSE(phone),
        bizType: PHONE_BIZTYPE,
        toast,
        sendChannel: 'VOICE',
      },
    });
  };

  const validateForVoice = () => {
    return Promise.resolve();
  };

  useEffect(() => {
    kcsensorsManualTrack({
      spm: ['activeConfirm', '1'],
      ...((trackingConfig && trackingConfig.data) || {}),
    });
  }, []);

  useEffect(() => {
    if (sendCodeSuc) handleTimer();
    // 清除timer
    return () => {
      clearInterval(timer);
    };
  }, [sendCodeSuc]);

  // 倒计时期间禁用语音验证
  useEffect(() => {
    if (PHONE_REGISTER_countTime && PHONE_REGISTER_countTime.time > 0) {
      setDisable(true);
    }
  }, [PHONE_REGISTER_countTime]);

  const renderSuffix = () => {
    if (forbidSentSeconds <= 0) {
      return (
        <RepeatSend
          theme={theme}
          disable={disable}
          onClick={() => {
            if (disable) return;
            handleSendCode();
          }}
        >
          {t('newsignup.code.retry')}
        </RepeatSend>
      );
    }
    return (
      <ClassNames>
        {({ css }) => {
          const ClassName1 = css`
            width: 242px;
            margin-right: -12px;
            padding: 5px 8px;
          `;

          return (
            <CodeSent theme={theme}>
              <SentText>{t('newsignup.code.sent')}</SentText>
              <Tooltip
                placement="top-end"
                tipClass={ClassName1}
                title={t('newsignup.code.notice2', { second: forbidSentSeconds || 0, minute: 10 })}
              >
                <InfoIconContainer>
                  <InfoOutlined style={{ cursor: 'pointer' }} />
                </InfoIconContainer>
              </Tooltip>
            </CodeSent>
          );
        }}
      </ClassNames>
    );
  };

  return (
    <Wrap>
      <Title>{t('newsignup.account.active')}</Title>
      <Tips theme={theme}>{t('newsignup.code.notice1', { account })}</Tips>
      <InputPreTips theme={theme}>{t('newsignup.code')}</InputPreTips>

      <Input
        size="large"
        suffix={renderSuffix()}
        value={inputVal}
        onChange={(e) => {
          setInputVal(e.target.value || '');
        }}
        onFocus={() => {
          setIsFoucs(true);
        }}
        onBlur={() => {
          setIsFoucs(false);
        }}
      />
      {isFocus && noInputVal ? <ErrorText theme={theme}>{_t('form.required')}</ErrorText> : null}
      <VoiceCode
        validateFunc={validateForVoice}
        phone={phone}
        email={email}
        countryCode={countryCode}
        countTime={sendChannel && sendChannel === 'VOICE' ? PHONE_REGISTER_countTime : null}
        loading={loading}
        disable={disable}
        onTimeOver={offDisabled}
        onSend={onSendVoice}
        trackingConfig={trackingConfig}
      />
      <Button fullWidth size="large" loading={submitLoading} onClick={handleSubmit}>
        {t('newsignup.account.active')}
      </Button>
    </Wrap>
  );
};

export default AccountActive;
